import { Typography } from "@mui/material";
import { memo, ReactNode, useState } from "react";
import { Box, Icon } from "../../../common/components";
import { BoxProps } from "../../../common/components/Box/Box";
import { MaterialSymbolType } from "../../../common/components/Icon/IconConstant";
import { createStyles, palette } from "../../../styles/theme";
import {
  getConditionnalStyle,
  useThemeBreakpoints,
} from "../../../utils/themeUtils";

const styles = createStyles({
  container: {
    display: "flex",
    padding: { xs: "2px 8px", lg: "6px 8px" },
    alignItems: "center",
    gap: "4px",
    borderRadius: "32px",
    cursor: "pointer",
    userSelect: "none",
    border: `1px solid transparent`,
    margin: "1px",
  },
  hover: {
    background: `linear-gradient(90deg, rgba(84, 172, 222, 0.04) 0%, rgba(169, 84, 222, 0.04) 100%), ${palette.lightBorderGradient}`,
    "#gradient-chip": {
      background: "unset",
    },
  },
  active: {
    margin: 0,
    border: `2px solid transparent`,
    background: `${palette.borderGradient}`,
  },
});

interface Props extends Omit<BoxProps, "title"> {
  title: string | ReactNode;
  icon: MaterialSymbolType;
  hoverAndActiveIcon?: MaterialSymbolType;
  iconSize?: number;
}

const StudioNavButton = ({
  title,
  icon,
  hoverAndActiveIcon,
  iconSize,
  ...boxProps
}: Props) => {
  const { isMobile } = useThemeBreakpoints();
  const [isHover, setIsHover] = useState(false);
  const [isActive, setIsActive] = useState(false);

  return (
    <Box
      {...boxProps}
      sx={[
        styles.container,
        getConditionnalStyle(isHover, styles.hover),
        getConditionnalStyle(isActive, styles.active),
      ]}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      onMouseOut={() => setIsActive(false)}
    >
      <Icon
        icon={isHover || isActive ? (hoverAndActiveIcon ?? icon) : icon}
        size={iconSize ?? (isMobile ? 24 : 20)}
      />
      {title && (
        <Typography
          sx={{
            color: "#222",
            fontSize: "12px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "20px",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
      )}
    </Box>
  );
};

export default memo(StudioNavButton);
